import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ShieldAlert, CheckCircle2, ArrowLeft, Copy, Check, Mail, Download, Loader2, XCircle, Circle, Clock } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * WarrantyClaimPage — public, signature-verified warranty draft.
 *
 * Reached one-tap from a Trust Score drop alert email. The owner
 * doesn't need to be logged in. We fetch the passport by ID + verify
 * its HMAC signature, then render a pre-filled claim body they can
 * copy or open in Mail with a single click.
 *
 * If the passport has ample warranty headroom, we show a friendly
 * "you probably don't need to file yet" message instead of the draft.
 */
export default function WarrantyClaimPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const sig = sp.get("sig") || "";

  const [state, setState] = useState({ loading: true, error: null, rec: null, verify: null });
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const p = await fetch(`${API_BASE}/api/battery/passport/${encodeURIComponent(id)}`).then((r) =>
          r.ok ? r.json() : null,
        );
        if (!p) throw new Error("not_found");
        let v = null;
        if (sig) {
          v = await fetch(`${API_BASE}/api/battery/verify/${encodeURIComponent(id)}?sig=${encodeURIComponent(sig)}`).then((r) => (r.ok ? r.json() : null));
        }
        if (!cancelled) setState({ loading: false, error: null, rec: p, verify: v });
      } catch (_e) {
        if (!cancelled) setState({ loading: false, error: "Passport not found or signature invalid.", rec: null, verify: null });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, sig]);

  if (state.loading) return <FullMsg msg="Loading claim draft…" />;
  if (state.error || !state.rec) return <FullMsg msg={state.error || "Passport not found."} error />;

  // The verify endpoint returned {valid: false} — refuse to draft off an
  // unverified passport so we never help someone file a fabricated claim.
  if (sig && state.verify && state.verify.valid === false) {
    return <FullMsg msg="Signature mismatch — cannot draft a claim from an unverifiable passport." error />;
  }

  const s = state.rec.snapshot || {};
  const soh = Number(s.soh_pct ?? 0);
  const floor = Number(s.warranty_threshold_pct ?? 70);
  const headroom = +(soh - floor).toFixed(1);
  const velocity = Math.abs(Number(s.delta_30d_pct ?? 0));
  const monthsToFloor = velocity > 0.001 ? Math.max(0, Math.round(headroom / velocity)) : 999;

  // Ample headroom + no rapid degradation → friendly wait-and-watch state.
  const notReady = headroom > 6 && !s.rapid_degradation_detected;

  return (
    <div className="min-h-screen text-white" style={{ background: "#050505" }} data-testid="warranty-claim-page">
      <div className="max-w-2xl mx-auto px-5 py-8">
        <Link
          to={`/passport/${id}${sig ? `?sig=${encodeURIComponent(sig)}` : ""}`}
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white inline-flex items-center gap-1.5 mb-6"
          data-testid="warranty-back-to-passport"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={2.4} />
          Back to passport
        </Link>

        {notReady ? (
          <NotReadyPanel s={s} headroom={headroom} monthsToFloor={monthsToFloor} />
        ) : (
          <ReadyPanel
            s={s}
            passportId={id}
            sig={sig}
            soh={soh}
            floor={floor}
            headroom={headroom}
            velocity={velocity}
            monthsToFloor={monthsToFloor}
            copied={copied}
            setCopied={setCopied}
            downloading={downloading}
            setDownloading={setDownloading}
          />
        )}
      </div>
    </div>
  );
}

// Derive the OEM from the passport snapshot's model string so Ford
// passports draft claims against Ford Service, not Tesla.
const oemFor = (s) =>
  /mach-e|f-150|lightning|e-transit|explorer ev|capri|puma|ford/i.test(s?.model || "") ? "Ford" : "Tesla";

const NotReadyPanel = ({ s, headroom, monthsToFloor }) => (
  <div
    className="rounded-2xl border p-6"
    style={{ borderColor: "rgba(57,255,20,0.35)", background: "rgba(57,255,20,0.05)" }}
    data-testid="warranty-not-ready"
  >
    <div className="flex items-center gap-2 mb-3">
      <CheckCircle2 className="w-5 h-5" style={{ color: "#39FF14" }} strokeWidth={2.4} />
      <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "#39FF14" }}>
        You probably don't need to file yet
      </div>
    </div>
    <div className="text-[13px] text-zinc-300 leading-relaxed mb-3">
      Your {oemFor(s)} still has <span className="text-white font-semibold">{headroom.toFixed(1)} points</span> of warranty
      headroom above {oemFor(s)}'s {s.warranty_threshold_pct || 70}% floor
      {monthsToFloor < 900 && (
        <> — at your current velocity, you're projected {monthsToFloor} months from the floor.</>
      )}
    </div>
    <div className="text-[12px] text-zinc-400 leading-relaxed">
      Keep monitoring. If SoH drops or velocity accelerates, EVIQ will
      email you again and the draft below will unlock.
    </div>
  </div>
);

const ReadyPanel = ({ s, passportId, sig, soh, floor, headroom, velocity, monthsToFloor, copied, setCopied, downloading, setDownloading }) => {
  const [claim, setClaim] = useState(null);
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimErr, setClaimErr] = useState(null);

  // Fetch existing claim status on mount so we can show the tracker if
  // the owner has already started their claim from a previous session.
  useEffect(() => {
    if (!passportId) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/warranty/claim/by_passport/${encodeURIComponent(passportId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled && j && j.exists) setClaim(j);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [passportId]);

  const ensureClaimStarted = async () => {
    if (claim) return claim;
    setClaimBusy(true);
    setClaimErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/warranty/claim/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          passport_id: passportId,
          sig,
          vin: s.vin,
          soh_pct: soh,
          headroom_pct: headroom,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Could not start claim");
      // Backend may return the raw doc (start) OR derived-augmented (get).
      // Refetch to get the derived fields (current_stage, progress_pct).
      const rr = await fetch(`${API_BASE}/api/warranty/claim/by_passport/${encodeURIComponent(passportId)}`);
      const jj = await rr.json();
      setClaim(jj);
      return jj;
    } catch (e) {
      setClaimErr(e.message || "Could not start claim");
      return null;
    } finally {
      setClaimBusy(false);
    }
  };

  const advanceTo = async (to_stage, extra = {}) => {
    setClaimBusy(true);
    setClaimErr(null);
    try {
      // If claim doesn't exist yet, start it first (advance requires a claim).
      let existing = claim;
      if (!existing) {
        existing = await ensureClaimStarted();
        if (!existing) return;
      }
      const r = await fetch(`${API_BASE}/api/warranty/claim/advance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passport_id: passportId, sig, to_stage, ...extra }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Could not update claim");
      setClaim(j);
    } catch (e) {
      setClaimErr(e.message || "Could not update claim");
    } finally {
      setClaimBusy(false);
    }
  };

  const subject = `Battery warranty inquiry — VIN ${s.vin} · SoH ${soh.toFixed(1)}%`;
  const emailBody = useMemo(() => [
    `To ${oemFor(s)} Service,`,
    "",
    `I own the ${oemFor(s)} listed below and I am submitting a warranty-relevant`,
    "battery health notification, as documented by an independent EVIQ",
    "Battery Passport (linked below).",
    "",
    "Vehicle",
    `  VIN:              ${s.vin || "—"}`,
    `  Model / Year:     ${s.year || "—"} ${s.model || "—"}${s.trim ? " · " + s.trim : ""}`,
    "",
    "Battery",
    `  Current SoH:      ${soh.toFixed(1)}%`,
    `  Warranty floor:   ${floor}%`,
    `  Headroom:         ${headroom.toFixed(1)} pts`,
    `  30-day trend:     -${velocity.toFixed(2)}% / month`,
    `  Projected to floor: ~${monthsToFloor} months at current velocity`,
    s.rapid_degradation_detected ? "  Rapid degradation: DETECTED" : null,
    "",
    `Please schedule a battery assessment at my nearest ${oemFor(s)} Service`,
    `Center and confirm whether coverage under the ${s.warranty_years || 8}-year /`,
    `${((s.warranty_miles || (oemFor(s) === "Ford" ? 100000 : 120000)) / 1000).toFixed(0)}k-mile high-voltage battery warranty applies.`,
    "",
    "The signed EVIQ Battery Passport for this vehicle is available at:",
    `https://eviq.pro/passport/${passportId}${sig ? `?sig=${sig}` : ""}`,
    "",
    "It uses an HMAC-SHA256 signature and can be independently verified.",
    "",
    "Thank you,",
    "",
  ].filter(Boolean).join("\n"), [s, soh, floor, headroom, velocity, monthsToFloor, passportId, sig]);

  const mailtoHref = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(emailBody);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  const onDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/api/battery/certify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(s),
      });
      if (!res.ok) throw new Error("certify_failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EVIQ_Warranty_Claim_${passportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { /* silent */ }
    finally { setDownloading(false); }
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center border"
          style={{ background: "rgba(255,107,87,0.14)", borderColor: "rgba(255,107,87,0.5)" }}
        >
          <ShieldAlert className="w-5 h-5" style={{ color: "#FF6B57" }} strokeWidth={2.3} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            EVIQ Warranty Claim · one-tap draft
          </div>
          <h1 className="font-display text-2xl font-semibold leading-tight" data-testid="warranty-title">
            Ready to send to {oemFor(s)} Service
          </h1>
          <div className="font-mono text-[10px] text-zinc-500 mt-1">
            Passport {passportId} · VIN {s.vin} · SoH {soh.toFixed(1)}% · headroom {headroom.toFixed(1)} pts
          </div>
        </div>
      </div>

      {/* Draft preview */}
      <div
        className="rounded-2xl border p-4 mb-4"
        style={{ borderColor: "rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.015)" }}
      >
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
          Subject
        </div>
        <div className="text-[13px] text-white font-semibold leading-tight mb-3" data-testid="warranty-subject">
          {subject}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
          Body
        </div>
        <pre
          className="text-[11.5px] text-zinc-200 leading-relaxed whitespace-pre-wrap font-mono max-h-[420px] overflow-y-auto p-3 rounded-md border"
          style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.35)" }}
          data-testid="warranty-body"
        >
{emailBody}
        </pre>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mb-6">
        <a
          href={mailtoHref}
          onClick={() => advanceTo("emailed")}
          data-testid="warranty-open-mail"
          className="rounded-full brand-gradient text-black px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 hover:opacity-90"
        >
          <Mail className="w-3.5 h-3.5" strokeWidth={2.4} />
          Open in Mail
        </a>
        <button
          type="button"
          onClick={onCopy}
          data-testid="warranty-copy"
          className="rounded-full border border-white/15 px-4 py-2 text-xs text-white inline-flex items-center gap-1.5 hover:bg-white/[0.05]"
        >
          {copied ? <Check className="w-3.5 h-3.5" style={{ color: "#39FF14" }} strokeWidth={2.6} /> : <Copy className="w-3.5 h-3.5" strokeWidth={2.4} />}
          {copied ? "Copied" : "Copy draft"}
        </button>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={downloading}
          data-testid="warranty-download-pdf"
          className="rounded-full border border-white/15 px-4 py-2 text-xs text-white inline-flex items-center gap-1.5 hover:bg-white/[0.05] disabled:opacity-50"
        >
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.2} /> : <Download className="w-3.5 h-3.5" strokeWidth={2.4} />}
          Attach signed PDF
        </button>
      </div>

      {/* Status Tracker — after "Open in Mail", the claim advances through
          Drafted → Emailed → Responded → In Service (countdown) → Complete. */}
      <ClaimTracker
        claim={claim}
        busy={claimBusy}
        err={claimErr}
        onAdvance={advanceTo}
      />

      <div className="text-[11px] text-zinc-500 leading-relaxed">
        Attaching the signed EVIQ Battery Passport (HMAC-SHA256) makes the
        claim independently verifiable. {oemFor(s)}'s warranty policy commits to a
        battery replacement or repair if capacity drops below {floor}% within
        the {s.warranty_years || 8}-year / {(s.warranty_miles || (oemFor(s) === "Ford" ? 100000 : 120000)).toLocaleString()}-mi window.
      </div>
    </>
  );
};

const FullMsg = ({ msg, error }) => (
  <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "#050505" }}>
    <div className="text-center max-w-md">
      {error ? (
        <XCircle className="w-10 h-10 mx-auto mb-3" style={{ color: "#FF6B57" }} strokeWidth={2} />
      ) : (
        <ShieldAlert className="w-10 h-10 mx-auto mb-3 animate-pulse" style={{ color: "#FFB020" }} strokeWidth={2} />
      )}
      <div className="text-white text-sm mb-4">{msg}</div>
      <Link to="/" className="text-[11px] font-mono uppercase tracking-wider text-zinc-400 hover:text-white">
        ← Back to EVIQ
      </Link>
    </div>
  </div>
);


// -------------------------------------------------------------------------
// ClaimTracker — shows the current stage in the claim pipeline, plus a
// countdown clock once the vehicle is in service.
// -------------------------------------------------------------------------
const STAGE_ORDER = ["drafted", "emailed", "responded", "in_service", "completed"];
const STAGE_LABEL = {
  drafted: "Drafted",
  emailed: "Emailed to Service",
  responded: "Service Responded",
  in_service: "In Service",
  completed: "Service Complete",
};

const ClaimTracker = ({ claim, busy, err, onAdvance }) => {
  const [etaDays, setEtaDays] = useState(7);

  if (!claim) {
    return (
      <div
        className="rounded-2xl border p-4 mb-6"
        style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.015)" }}
        data-testid="warranty-claim-tracker-empty"
      >
        <div className="flex items-center gap-2 mb-1">
          <Circle className="w-3 h-3 text-zinc-400" strokeWidth={2.6} />
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-400">
            Claim status · Not yet started
          </div>
        </div>
        <div className="text-[12px] text-zinc-400 leading-relaxed">
          When you hit <span className="text-white font-semibold">Open in Mail</span> above, we'll start tracking your claim through the manufacturer's service channel so you always know where it stands.
        </div>
        {err && <div className="text-[11px] text-[#FF6B57] font-mono mt-2">{err}</div>}
      </div>
    );
  }

  const currentIdx = STAGE_ORDER.indexOf(claim.current_stage || "drafted");
  const countdown = claim.countdown;
  const nextStage = STAGE_ORDER[currentIdx + 1] || null;
  const isInService = claim.current_stage === "in_service";
  const isCompleted = claim.current_stage === "completed";

  return (
    <div
      className="rounded-2xl border p-4 mb-6"
      style={{
        borderColor: isCompleted ? "rgba(57,255,20,0.35)" : "rgba(0,229,255,0.28)",
        background: isCompleted ? "rgba(57,255,20,0.05)" : "rgba(0,229,255,0.045)",
      }}
      data-testid="warranty-claim-tracker"
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: isCompleted ? "#39FF14" : "#00E5FF" }}
        />
        <div
          className="font-mono text-[10px] uppercase tracking-[0.22em]"
          style={{ color: isCompleted ? "#39FF14" : "#00E5FF" }}
        >
          Claim status · {claim.progress_pct}% complete
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden mb-4">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${claim.progress_pct}%`,
            background: isCompleted
              ? "linear-gradient(90deg, #39FF14 0%, #00E5FF 100%)"
              : "linear-gradient(90deg, #00E5FF 0%, #7FD1FF 100%)",
          }}
          data-testid="warranty-claim-progress"
        />
      </div>

      {isInService && countdown && (
        <div
          className="rounded-lg border p-3 mb-4 flex items-center gap-3"
          style={{ borderColor: "rgba(255,176,32,0.4)", background: "rgba(255,176,32,0.08)" }}
          data-testid="warranty-claim-countdown"
        >
          <Clock className="w-5 h-5 shrink-0" style={{ color: "#FFB020" }} strokeWidth={2.4} />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
              Service complete in
            </div>
            <div className="text-white font-display text-2xl font-semibold leading-tight">
              {countdown.is_overdue
                ? "Overdue"
                : `${countdown.days_left_ceil} ${countdown.days_left_ceil === 1 ? "day" : "days"}`}
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
              Estimated ready {new Date(countdown.ready_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
            </div>
          </div>
        </div>
      )}

      <ol className="space-y-2 mb-3" data-testid="warranty-claim-stages">
        {claim.stages.map((st) => {
          const done = st.status === "done";
          const active = STAGE_ORDER.indexOf(st.key) === currentIdx;
          return (
            <li key={st.key} className="flex items-start gap-3">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{
                  background: done ? "#39FF1418" : "rgba(255,255,255,0.04)",
                  border: `1.5px solid ${done ? "#39FF14" : "rgba(255,255,255,0.15)"}`,
                }}
              >
                {done && <Check className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={3} />}
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-[13px] leading-tight ${done ? "text-white font-semibold" : active ? "text-zinc-200" : "text-zinc-500"}`}>
                  {STAGE_LABEL[st.key]}
                </div>
                {st.at && (
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                    {new Date(st.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {!isCompleted && nextStage && (
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/[0.06] mt-3">
          {nextStage === "in_service" ? (
            <>
              <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-400 mr-1">
                ETA
              </label>
              <input
                type="number"
                min={1}
                max={90}
                value={etaDays}
                onChange={(e) => setEtaDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
                className="w-14 bg-black/40 border border-white/10 rounded px-2 py-1 text-[12px] text-white font-mono text-center"
                data-testid="warranty-eta-input"
              />
              <span className="font-mono text-[10px] text-zinc-400">days</span>
              <button
                type="button"
                onClick={() => onAdvance(nextStage, { estimated_service_days: etaDays })}
                disabled={busy}
                className="rounded-full border border-white/15 px-3 py-1.5 text-[11px] text-white hover:bg-white/[0.06] disabled:opacity-50"
                data-testid="warranty-mark-in-service"
              >
                Mark: Vehicle in Service
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onAdvance(nextStage)}
              disabled={busy}
              className="rounded-full border border-white/15 px-3 py-1.5 text-[11px] text-white hover:bg-white/[0.06] disabled:opacity-50"
              data-testid={`warranty-advance-${nextStage}`}
            >
              Mark: {STAGE_LABEL[nextStage]}
            </button>
          )}
        </div>
      )}
      {isCompleted && (
        <div className="text-[11px] text-[#39FF14] font-mono pt-2 border-t border-white/[0.06] mt-2" data-testid="warranty-completed-note">
          ✓ Claim closed. Nice work.
        </div>
      )}
      {err && <div className="text-[11px] text-[#FF6B57] font-mono mt-2">{err}</div>}
    </div>
  );
};
